import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-loading';
import {
  CompanyCreateResponseDto,
  CompanyDetailRequestDto,
  CompanyListResponseDto,
  CompanyDetailResponseDto,
  CompanyUpdateResponseDto,
} from './dtos/company.dto';
import { CompanyMapper } from './mappers/company.mapper';
import { CompanyDetailMapper } from './mappers/company-detail.mapper';
import { CompanyPayloadMapper } from './mappers/company-payload.mapper';
import {
  CompaniesPage,
  CompanyDetail,
  CompanyDetailPayload,
  CompanySaveResult,
} from '../models/company.model';

@Injectable({ providedIn: 'root' })
export class CompanyApi {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentConfig = inject(ENV_CONFIG);
  private readonly _baseUrl = this._environmentConfig.pureSpectrum.url;
  private readonly _mapper = new CompanyMapper();
  private readonly _detailMapper = new CompanyDetailMapper();
  private readonly _payloadMapper = new CompanyPayloadMapper();

  public getAll(
    page?: number,
    limit?: number,
    search?: string,
  ): Observable<CompaniesPage> {
    let params = new HttpParams();

    if (page != null) {
      params = params.set('page', page.toString());
    }

    if (limit != null) {
      params = params.set('limit', limit.toString());
    }

    if (search) {
      params = params.set('search', search);
    }

    return this._httpClient
      .get<CompanyListResponseDto>(`${this._baseUrl}/companies/v2`, {
        params,
        context: withLoading('content'),
      })
      .pipe(
        map((response) => ({
          companies: this._mapper.fromDtoList(response.companies),
          count: response.count,
        })),
      );
  }

  public companyDetail(id: number): Observable<CompanyDetail> {
    return this._httpClient
      .get<CompanyDetailResponseDto>(`${this._baseUrl}/companies/v2/${id}`, {
        context: withLoading('content'),
      })
      .pipe(map((dto) => this._detailMapper.fromDto(dto)));
  }

  public saveCompany(
    payload: CompanyDetailPayload,
  ): Observable<CompanySaveResult> {
    return this._httpClient
      .post<CompanyCreateResponseDto>(
        `${this._baseUrl}/company`,
        this._payloadMapper.toPayload(payload),
        { context: withLoading('page') },
      )
      .pipe(
        map((response) => ({
          id: response.company.id,
          message: response.msg,
        })),
      );
  }

  public updateCompany(
    id: number,
    payload: CompanyDetailPayload,
  ): Observable<string> {
    type CompanyUpdateRequestDto = CompanyDetailRequestDto & { id: number };
    const body: CompanyUpdateRequestDto = {
      ...this._payloadMapper.toPayload(payload),
      id,
    };
    return this._httpClient
      .put<CompanyUpdateResponseDto>(`${this._baseUrl}/company/${id}`, body, {
        context: withLoading('page'),
      })
      .pipe(map((response) => response.msg));
  }

  public deleteCompany(id: number): Observable<void> {
    return this._httpClient
      .delete<void>(`${this._baseUrl}/company/${id}`, {
        context: withLoading('page'),
      })
      .pipe(map(() => undefined));
  }
}
